import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Button, Spinner, Alert } from "react-bootstrap";
import { FaEdit, FaPrint, FaArrowLeft } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import "./AddPurchase.css";

const ViewProforma = () => {
  const [proforma, setProforma] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { id } = useParams();
  const navigate = useNavigate();

  // Fetch proforma data from API
  useEffect(() => {
    const fetchProforma = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await api.post("/proforma_detail.php", { id });
        
        if (response.data.head.code === 200) {
          setProforma(response.data.body);
        } else {
          setError(response.data.head.msg || "Failed to fetch proforma details");
        }
      } catch (err) {
        console.error("Error fetching proforma:", err);
        setError("Failed to load proforma details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProforma();
    }
  }, [id]);

  const handlePrint = () => {
    if (proforma) {
      // Use window.open to open in new tab for printing
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Proforma Invoice #${proforma.id}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 40px; }
              .header { text-align: center; margin-bottom: 30px; }
              .details { margin-bottom: 30px; }
              table { width: 100%; border-collapse: collapse; margin: 20px 0; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; }
              .total-section { margin-top: 30px; text-align: right; }
              .total-row { margin: 5px 0; }
              .grand-total { font-size: 1.2em; font-weight: bold; }
              .status { padding: 5px 10px; border-radius: 4px; display: inline-block; }
              .status-draft { background-color: #ffc107; color: #000; }
              .status-sent { background-color: #17a2b8; color: #fff; }
              .status-accepted { background-color: #28a745; color: #fff; }
              .status-rejected { background-color: #dc3545; color: #fff; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Proforma Invoice</h1>
              <h3>PRO-${proforma.id}</h3>
              <p>Date: ${proforma.date}</p>
            </div>
            
            <div class="details">
              <h3>Customer Details</h3>
              <p><strong>Name:</strong> ${proforma.customer_name || "N/A"}</p>
              <p><strong>Phone:</strong> ${proforma.customer_phone || "N/A"}</p>
              <p><strong>Address:</strong> ${proforma.customer_address || proforma.customer_address || "N/A"}</p>
              ${proforma.customer_gst_no ? `<p><strong>GST No:</strong> ${proforma.customer_gst_no}</p>` : ''}
              <p><strong>Status:</strong> <span class="status status-${proforma.status?.toLowerCase()}">${proforma.status}</span></p>
            </div>
            
            <h3>Items</h3>
            <table>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Product Code</th>
                  <th>Item</th>
                  <th>Selling Rate (₹)</th>
                  <th>Qty</th>
                  <th>Total (₹)</th>
                </tr>
              </thead>
              <tbody>
                ${proforma.items?.map((item, index) => `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${item.product_code}</td>
                    <td>${item.product_name}</td>
                    <td>₹${parseFloat(item.selling_rate).toFixed(2)}</td>
                    <td>${item.quantity}</td>
                    <td>₹${parseFloat(item.total).toFixed(2)}</td>
                  </tr>
                `).join('') || '<tr><td colspan="6">No items</td></tr>'}
              </tbody>
            </table>
            
            <div class="total-section">
              <div class="total-row">
                <strong>Subtotal:</strong> ₹${parseFloat(proforma.sub_total || 0).toFixed(2)}
              </div>
              <div class="total-row">
                <strong>GST (${proforma.gst_percentage}%):</strong> ₹${parseFloat(proforma.gst_amount || 0).toFixed(2)}
              </div>
              <div class="total-row grand-total">
                <strong>Grand Total:</strong> ₹${parseFloat(proforma.grand_total || 0).toFixed(2)}
              </div>
            </div>
            
            ${proforma.notes ? `<div style="margin-top: 30px;"><strong>Notes:</strong> ${proforma.notes}</div>` : ''}
            
            <script>
              window.onload = function() {
                window.print();
                setTimeout(function() {
                  window.close();
                }, 1000);
              }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  // Format ID as PRO-{id}
  const formatProformaId = (id) => {
    return `PRO-${id}`;
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Proforma Details</h4>
          <Button className="btn-top" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Back
          </Button>
        </div>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading proforma details...</p>
        </div>
      </Container>
    );
  }

  if (error || !proforma) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Proforma Details</h4>
          <Button className="btn-top" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Back
          </Button>
        </div>
        <Alert variant="danger">
          {error || "Proforma not found."}
        </Alert>
        <Button className="mt-3" onClick={() => navigate("/proforma-list")}>
          Back to Proforma List
        </Button>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Proforma Details</h4>
        <div className="d-flex gap-2">
          <Button className="btn-top" onClick={handlePrint}>
            <FaPrint className="me-2" /> Print
          </Button>
          <Button
            className="btn-submit"
            onClick={() => navigate(`/editproforma/${proforma.id}`)}
          >
            <FaEdit className="me-2" /> Edit
          </Button>
          <Button className="btn-top" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Back
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {/* Proforma ID, Date and Status Section */}
      <div className="p-4 border rounded bg-light mb-4">
        <Row>
          <Col lg={4} md={4} sm={12}>
            <div className="mb-3">
              <strong>Proforma ID:</strong>
              <div className="fs-5 text-primary">{formatProformaId(proforma.id)}</div>
            </div>
          </Col>
          <Col lg={4} md={4} sm={12}>
            <div className="mb-3">
              <strong>Date:</strong>
              <div className="fs-5">{proforma.date}</div>
            </div>
          </Col>
          <Col lg={4} md={4} sm={12}>
            <div className="mb-3">
              <strong>Status:</strong>
              <div className="fs-5">
                <span className={`badge bg-${getStatusColor(proforma.status)}`}>
                  {proforma.status}
                </span>
              </div>
            </div>
          </Col>
          {proforma.quotation_id && (
            <Col lg={12} md={12} sm={12}>
              <div className="mb-3">
                <strong>Quotation Reference:</strong>
                <div className="fs-6">{proforma.quotation_id}</div>
              </div>
            </Col>
          )}
        </Row>
      </div>

      {/* Customer Details Section */}
      <div className="p-4 border rounded bg-light mb-4">
        <Row>
          <Col lg={6} md={6} sm={12}>
            <div className="mb-3">
              <strong>Customer Name:</strong>
              <div className="fs-5">{proforma.customer_name || "N/A"}</div>
            </div>
          </Col>
          <Col lg={6} md={6} sm={12}>
            <div className="mb-3">
              <strong>Phone Number:</strong>
              <div className="fs-5">{proforma.customer_phone || "N/A"}</div>
            </div>
          </Col>
          <Col lg={12} md={12} sm={12}>
            <div className="mb-3">
              <strong>Address:</strong>
              <div className="fs-6">{proforma.customer_address || "N/A"}</div>
            </div>
          </Col>
          {proforma.customer_gst_no && (
            <Col lg={6} md={6} sm={12}>
              <div className="mb-3">
                <strong>GST Number:</strong>
                <div className="fs-6">{proforma.customer_gst_no}</div>
              </div>
            </Col>
          )}
          {proforma.customer_state && (
            <Col lg={6} md={6} sm={12}>
              <div className="mb-3">
                <strong>State:</strong>
                <div className="fs-6">{proforma.customer_state}</div>
              </div>
            </Col>
          )}
        </Row>
      </div>

      {/* Proforma Items Table */}
      <div className="table-responsive">
        <Table bordered className="purchase-table">
          <thead className="table-dark">
            <tr>
              <th width="5%" className="text-center">S.No</th>
              <th width="15%">Product Code</th>
              <th width="25%">Item</th>
              <th width="15%">Selling Rate (₹)</th>
              <th width="10%">Qty</th>
              <th width="15%" className="text-end">Total (₹)</th>
            </tr>
          </thead>
          <tbody>
            {proforma.items && proforma.items.length > 0 ? (
              proforma.items.map((item, index) => (
                <tr key={item.id || index}>
                  <td className="text-center">{index + 1}</td>
                  <td>{item.product_code}</td>
                  <td>{item.product_name}</td>
                  <td>₹{parseFloat(item.selling_rate || 0).toFixed(2)}</td>
                  <td>{item.quantity}</td>
                  <td className="text-end">₹{parseFloat(item.total || 0).toFixed(2)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center text-muted py-3">
                  No items found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Totals Section */}
      <Row className="mt-4">
        <Col xs={12} className="d-flex justify-content-end">
          <div className="totals-section">
            <div className="total-row">
              <span className="total-label">Subtotal:</span>
              <span className="total-value">₹{parseFloat(proforma.sub_total || 0).toFixed(2)}</span>
            </div>
            <div className="total-row">
              <span className="total-label">
                GST ({proforma.gst_percentage || 0}%)
              </span>
              <span className="total-value">₹{parseFloat(proforma.gst_amount || 0).toFixed(2)}</span>
            </div>
            <div className="total-row grand-total">
              <span className="total-label">Grand Total:</span>
              <span className="total-value">₹{parseFloat(proforma.grand_total || 0).toFixed(2)}</span>
            </div>
          </div>
        </Col>
      </Row>

      {/* Notes Section */}
      {proforma.notes && (
        <Row className="mt-4">
          <Col xs={12}>
            <div className="p-3 border rounded bg-light">
              <strong>Notes:</strong>
              <div className="mt-2">{proforma.notes}</div>
            </div>
          </Col>
        </Row>
      )}

      {/* Action Buttons */}
      <div className="d-flex justify-content-end mt-4 gap-2">
        <Button className="btn-top" onClick={handlePrint}>
          <FaPrint className="me-2" /> Print Proforma
        </Button>
        <Button
          className="btn-submit"
          onClick={() => navigate(`/editproforma/${proforma.id}`)}
        >
          <FaEdit className="me-2" /> Edit Proforma
        </Button>
        <Button className="btn-top" onClick={() => navigate("/proforma-list")}>
          <FaArrowLeft className="me-2" /> Back to List
        </Button>
      </div>
    </Container>
  );
};

// Helper function for status colors
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'draft': return 'warning';
    case 'sent': return 'info';
    case 'accepted': return 'success';
    case 'rejected': return 'danger';
    default: return 'secondary';
  }
};

export default ViewProforma;